import { ConfigurationsContext } from '../App';
import { ApplicationContext, PreferencesContext } from '../App';
import { useMachineConfig } from './General';
import { LayoutContent } from './Layout';
import {
	KeyboardContext,
	KeyboardTrigger,
	SelectContext,
	SelectTrigger,
} from './Layout';
import { Loading } from './Layout';
import { TabOption } from './Sidebar';
import BoxSize from './pallet_config/Boxes';
import Layout from './pallet_config/Layouts/Layouts';
import Pallets from './pallet_config/Pallets';
import Stack from './pallet_config/Stacks/Stacks';
import { createDefaultSlipSheetConfig } from './pallet_config/Stacks/stackUtils';
import ConfigurationSummary from './pallet_config/Summary';
import { ControlProps, isWebDeployment } from './shared/shared';
import {
	Pallet_t,
	Box_t,
	Layout_t,
	PalletConfiguration,
	SavedPalletConfiguration,
	Stack as StackObject,
	newPalletConfiguration,
	getDefaultPallets,
	Layer_t,
	SlipSheet_t,
} from 'blackbox';
import { API, Application, PalletConfig, UnitsSystem } from 'libapi';
import React, {
	useReducer,
	useState,
	ReactElement,
	useEffect,
	useContext,
	createRef,
	useRef,
	useCallback,
} from 'react';
import {
	Switch,
	Link,
	Route,
	useRouteMatch,
	Redirect,
	useLocation,
} from 'react-router-dom';
import { Unlock } from './Modal';
import SlipSheetSize from './pallet_config/SlipSheets';
import { Calibration_t, to_calibration } from './calibration/Calibration';
import { v4 as uuidv4 } from 'uuid';

interface ConfigurationPageProps {
	pTab: TabOption;
}

export default function Configuration({ pTab }: ConfigurationPageProps) {
	const { all_configs, set_all_configs, reload_cfs } = useContext(
		ConfigurationsContext
	) as any;
	const props: PalletConfigProps = {
		back: function () {
			reload_cfs();
		},
		id: 0,
		all_configs,
		set_all_configs,
		pTab,
	};
	const [locked, setLocked] = useState<boolean>(true);

	return (
		<LayoutContent pTab={pTab} hideHeader={true}>
			<>
				{all_configs ? (
					<PalletConfigurationContainer {...props} />
				) : (
					<div className="flex-center flex-col">
						<Loading />
						<h3 className="padding dark-blue"> Loading configurations...</h3>
					</div>
				)}
				{locked && (
					<Unlock
						close={() => {
							setLocked(false);
						}}
					></Unlock>
				)}
			</>
		</LayoutContent>
	);
}

/*
 * BoxReducer is intended to handle removing dependant items (layouts, stacks) when
 * a box is removed or modified.
 */

interface BoxReducer {
	reduce(
		pState: PalletConfiguration,
		pBoxes: Box_t[],
		api: API
	): PalletConfiguration;
}

const BoxReducer = (function () {
	function getModifiedBoxIndex(
		pNewBoxes: Box_t[],
		pPreviousBoxes: Box_t[]
	): number | null {
		if (pPreviousBoxes.length < pNewBoxes.length) return null; // box added
		for (let i = 0; i < pNewBoxes.length; i++) {
			let lBox = pNewBoxes[i];
			if (lBox.changed) return i;
			if (lBox.deleted) return i;
		}
		return null;
	}

	interface DependantItem {
		palletIndex: number;
		itemIndex: number;
	}

	// used to map old index -> new index in layouts / stacks.
	type IndexMap = { [oldIndex: number]: number };
	// used to map pallet index -> index map for all layouts / stacks in pallet.
	type PalletIndexMap = { [palletIndex: number]: IndexMap };

	// removes dependant layous and returns a list of removed layouts.
	// also returns a mapping of pallet index -> layout index -> new layout index.
	// and, as a side effect, shifts the layout.boxPositions.index to account for deleted box.
	function findDependantLayouts(
		pConfig: PalletConfiguration,
		pModifiedBoxIndex: number,
		lRemovedBox: boolean // used for box index shifting.
	): [DependantItem[], PalletIndexMap] {
		// find layouts that use the modified box index.
		let lDependantLayouts: DependantItem[] = [],
			lPalletIndexMap: PalletIndexMap = {};
		pConfig.pallets.forEach(function (pPallet: Pallet_t, pPalletIndex: number) {
			let lNewLayoutIndexMap: IndexMap = {};
			let lRemovedCount = 0;
			pPallet.Layouts.forEach(function (pLayout: Layout_t, pLayoutIndex: number) {
				for (
					let lBoxPositionIndex = 0;
					lBoxPositionIndex < pLayout.boxPositions.length;
					lBoxPositionIndex++
				) {
					let lBoxPosition = pLayout.boxPositions[lBoxPositionIndex];
					if (pModifiedBoxIndex !== lBoxPosition.index) {
						if (lRemovedBox && lBoxPosition.index > pModifiedBoxIndex)
							lBoxPosition.index -= 1; // shift indices that come after removed box.
						continue;
					}
					lDependantLayouts.push({
						palletIndex: pPalletIndex,
						itemIndex: pLayoutIndex,
					});
					lRemovedCount++;
					break;
				}
				lNewLayoutIndexMap[pLayoutIndex] = pLayoutIndex - lRemovedCount;
			});
			lPalletIndexMap[pPalletIndex] = lNewLayoutIndexMap;
		});
		return [lDependantLayouts, lPalletIndexMap];
	}

	function removeDependantLayouts(
		pConfig: PalletConfiguration,
		pDepedantLayouts: DependantItem[]
	) {
		if (pDepedantLayouts.length === 0) return;
		pConfig.pallets.forEach(function (pPallet: Pallet_t, pPalletIndex: number) {
			pPallet.Layouts = pPallet.Layouts.filter(function (
				_pLayout: Layout_t,
				pLayoutIndex: number
			) {
				for (
					let lDependantIndex = 0;
					lDependantIndex < pDepedantLayouts.length;
					lDependantIndex++
				) {
					const lDependantLayout = pDepedantLayouts[lDependantIndex];
					if (
						!(
							pPalletIndex === lDependantLayout.palletIndex &&
							pLayoutIndex === lDependantLayout.itemIndex
						)
					)
						continue; // pallet index + layout index does not match.
					pDepedantLayouts.splice(lDependantIndex, 1); // remove dependant item.
					return false; // remove this layout.
				}
				return true;
			});
		});
	}

	function findDependantStacks(
		pConfig: PalletConfiguration,
		pDependantLayouts: DependantItem[]
	): DependantItem[] {
		let lDependantStacks: DependantItem[] = [];
		pDependantLayouts.forEach(function ({
			palletIndex: pPalletIndex,
			itemIndex: pLayoutIndex,
		}) {
			const lPallet = pConfig.pallets[pPalletIndex];
			lPallet.Stack.forEach(function (pStack: StackObject, pStackIndex: number) {
				for (
					let lLayerIndex = 0;
					lLayerIndex < pStack.layers.length;
					lLayerIndex++
				) {
					const lLayer = pStack.layers[lLayerIndex];
					if (pLayoutIndex !== lLayer.index) continue;
					lDependantStacks.push({
						palletIndex: pPalletIndex,
						itemIndex: pStackIndex,
					});
					break;
				}
			});
		});
		return lDependantStacks;
	}

	function removeDependantStacks(
		pConfig: PalletConfiguration,
		pDepedantStacks: DependantItem[],
		pPalletLayoutIndexMap: PalletIndexMap
	) {
		if (pDepedantStacks.length === 0) return;
		pConfig.pallets.forEach(function (pPallet: Pallet_t, pPalletIndex: number) {
			pPallet.Stack = pPallet.Stack.filter(function (
				pStack: StackObject,
				pStackIndex: number
			) {
				for (
					let lDependantIndex = 0;
					lDependantIndex < pDepedantStacks.length;
					lDependantIndex++
				) {
					const lDependantStack = pDepedantStacks[lDependantIndex];
					if (
						!(
							pPalletIndex === lDependantStack.palletIndex &&
							pStackIndex === lDependantStack.itemIndex
						)
					)
						continue; // pallet index + stack index does not match.
					pDepedantStacks.splice(lDependantIndex, 1); // remove dependant stack.
					return false;
				}
				// this stack does not need to be deleted, adjust layout indices for each layer
				// using the pallet -> layout index map.
				let lLayoutIndexMap = pPalletLayoutIndexMap[pPalletIndex];
				pStack.layers.forEach(function (pLayer: Layer_t) {
					pLayer.index = lLayoutIndexMap[pLayer.index];
				});
				return true;
			});
		});
	}

	function removeLayoutAndStackDependencies(
		pConfig: PalletConfiguration,
		pChangedBoxIndex: number,
		pDidRemoveBox: boolean
	) {
		let [lDependantLayouts, lPalletLayoutIndexMap] = findDependantLayouts(
				pConfig,
				pChangedBoxIndex,
				pDidRemoveBox
			),
			lDepedantStacks = findDependantStacks(pConfig, lDependantLayouts);
		removeDependantLayouts(pConfig, lDependantLayouts);
		removeDependantStacks(pConfig, lDepedantStacks, lPalletLayoutIndexMap);
	}

	function handleBoxModificationFlags(
		pNewBoxes: Box_t[],
		pChangedBoxIndex: number
	): boolean {
		const lChangedBox = pNewBoxes[pChangedBoxIndex];
		if (lChangedBox.deleted) {
			pNewBoxes.splice(pChangedBoxIndex, 1); // delete box.
			return true; // did remove.
		}
		if (lChangedBox.changed) {
			// remove flag properties from saved object.
			delete lChangedBox.changed;
			delete lChangedBox.deleted;
			return false; // did not remove
		}
		throw new Error('no box changes'); // should not occur.
	}

	function removeDependenciesOfChangedBox(
		pState: PalletConfiguration,
		pNewBoxes: Box_t[],
		pChangedBoxIndex: number,
		api: API
	) {
		let lDidRemoveBox = handleBoxModificationFlags(pNewBoxes, pChangedBoxIndex);
		removeLayoutAndStackDependencies(pState, pChangedBoxIndex, lDidRemoveBox);
		if (lDidRemoveBox) {
			const id = pState.boxes[pChangedBoxIndex].id;
			api.SKUSpecific.delete(id);
			console.log('DID REMOVE BOX!!!!', pState, pChangedBoxIndex);
		}
	}

	function reduce(
		state: PalletConfiguration,
		boxes: Box_t[],
		api: API
	): PalletConfiguration {
		let lPreviousBoxes = state.boxes,
			lChangedIndex: number | null = null;

		lChangedIndex = getModifiedBoxIndex(boxes, lPreviousBoxes);
		// no boxes modified (new box created or no change, continue).
		if (null === lChangedIndex) return { ...state, boxes };
		removeDependenciesOfChangedBox(state, boxes, lChangedIndex, api);
		return { ...state, boxes };
	}

	return {
		reduce,
	} as BoxReducer;
})();

interface SlipSheetReducer {
	reduce(
		pState: PalletConfiguration,
		pSlipSheets: SlipSheet_t[],
		api: API
	): PalletConfiguration;
}

const SlipSheetReducer = (function () {
	function getModifiedSlipSheetIndex(
		pNewSlipSheets: SlipSheet_t[],
		pPreviousSlipSheets: SlipSheet_t[]
	): number | null {
		if (pPreviousSlipSheets.length < pNewSlipSheets.length) return null; // ss added
		for (let i = 0; i < pNewSlipSheets.length; i++) {
			let lSS = pNewSlipSheets[i];
			if (lSS.deleted) return i;
		}
		return null;
	}
	function removeIdsFromCalibration(pIdToRemove: string, api: API) {
		api.Generic.get('CALIBRATION').then(function (data) {
			const c = to_calibration(data) as Calibration_t;
			let removed = false;
			Object.keys(c).forEach(function (k: any) {
				const k_t = k as keyof Calibration_t;
				const cal = c[k_t];
				if ('ss_ids' in cal) {
					const ss_ids = cal.ss_ids;
					const index = ss_ids.findIndex(id => id === pIdToRemove);
					if (index !== -1) {
						ss_ids.splice(index, 1);
						removed = true;
					}
				}
			});

			if (removed) {
				api.Generic.set(['CALIBRATION'], c)
					.then(function (result) {
						console.log('saved calibration', result.status);
					})
					.catch(console.error);
			}
		});
	}
	function removeFromStacks(pIdToRemove: string, pConfig: PalletConfiguration) {
		const pallets = pConfig.pallets;
		for (const pallet of pallets) {
			const stacks = pallet.Stack;
			for (const stack of stacks) {
				if (stack?.slipSheet) {
					if (Object.values(stack.slipSheet.ids).includes(pIdToRemove)) {
						// remove ss from stack
						stack.slipSheet = createDefaultSlipSheetConfig();
						break;
					}
				}
			}
		}
	}
	function removeSlipSheet(pIdToRemove: string, pSlipSheets: SlipSheet_t[]) {
		const index = pSlipSheets.findIndex(ss => ss.id === pIdToRemove);
		if (index !== -1) {
			pSlipSheets.splice(index, 1);
		}
	}
	function reduce(
		state: PalletConfiguration,
		slipsheets: SlipSheet_t[],
		api: API
	): PalletConfiguration {
		let lPreviousSlipSheets = state.slipsheets;

		const lChangedIndex = getModifiedSlipSheetIndex(
			slipsheets,
			lPreviousSlipSheets
		);
		if (lChangedIndex === null) return { ...state, slipsheets }; // No change
		const pIdToRemove = slipsheets[lChangedIndex].id;
		removeIdsFromCalibration(pIdToRemove, api);
		removeFromStacks(pIdToRemove, state);
		removeSlipSheet(pIdToRemove, slipsheets);
		return { ...state, slipsheets };
	}

	return {
		reduce,
	} as SlipSheetReducer;
})();

enum EnumConfAction {
	SetName,
	SetBoxes,
	SetPallets,
	SetSlipSheets,
}

type ConfigAction = {
	type: EnumConfAction;
	payload: any;
};

function configurationReducer(
	state: PalletConfiguration,
	action: ConfigAction,
	api: API
) {
	const { payload } = action;
	switch (action.type) {
		case EnumConfAction.SetName:
			return { ...state, name: payload as string };
		case EnumConfAction.SetBoxes:
			return BoxReducer.reduce(state, payload as Box_t[], api); // handle dependency updates across configuration.
		case EnumConfAction.SetPallets:
			return { ...state, pallets: payload as Pallet_t[] };
		case EnumConfAction.SetSlipSheets:
			return SlipSheetReducer.reduce(state, payload as SlipSheet_t[], api);
		default:
			return state;
	}
}

export function GenerateFinalConfig(
	startingConfig: PalletConfiguration
): SavedPalletConfiguration {
	const config = { ...startingConfig };
	return {
		config,
		boxCoordinates: [],
		complete: true,
	} as SavedPalletConfiguration;
}

export interface PalletConfigProps {
	id: number;
	all_configs: PalletConfig;
	set_all_configs: (x: PalletConfig) => {};
	back: () => void;
	pTab: TabOption;
}

export enum ConfigStep {
	Boxes,
	SlipSheets,
	Pallets,
	Layouts,
	Stacks,
	Summary,
}

const AllStepNames = [
	'Boxes',
	'Slip Sheets',
	'Pallets',
	'Layouts',
	'Stacks',
	'Summary',
];

const AllSteps = [
	ConfigStep.Boxes,
	ConfigStep.SlipSheets,
	ConfigStep.Pallets,
	ConfigStep.Layouts,
	ConfigStep.Stacks,
	ConfigStep.Summary,
];

function getPathForStep(pStep: ConfigStep) {
	return `/${AllStepNames[pStep]}`;
}

function getPathForNextStep(pStep: ConfigStep, slipsheetsPresent: boolean) {
	if (pStep === ConfigStep.SlipSheets && !slipsheetsPresent) {
		pStep += 1;
	}
	return getPathForStep(pStep);
}

const getAllowedStep = (pc: PalletConfiguration): ConfigStep => {
	if (pc.boxes.length === 0) return ConfigStep.Boxes;
	// if (Object.keys(pc.slipsheets).length === 0) return ConfigStep.SlipSheets;
	if (pc.pallets.length === 0) return ConfigStep.Pallets;
	if (pc.pallets.map(p => p.Layouts.length).filter(x => x > 0).length === 0)
		return ConfigStep.Layouts;
	if (pc.pallets.map(p => p.Stack.length).filter(x => x > 0).length === 0)
		return ConfigStep.Stacks;
	return ConfigStep.Summary;
};

export interface ConfigurationProps extends ControlProps {
	allBoxes: Box_t[];
	allPallets: Pallet_t[];
	allSlipSheets: SlipSheet_t[];
	setPallets(pNewPallets: Pallet_t[]): void;
	setBoxes(pNewBoxes: Box_t[]): void;
	setSlipSheets(pNewSlipSheets: SlipSheet_t[]): void;
	unitsSystem: UnitsSystem;
	triggerKeyboard: KeyboardTrigger;
	triggerSelect: SelectTrigger;
}

export interface BoxConfigurationProps extends ConfigurationProps {
	disableNext: boolean;
	requireWeight: boolean;
	showLegend: boolean;
}

function PalletConfigurationContainer({
	back,
	id,
	all_configs,
	set_all_configs,
	pTab,
}: PalletConfigProps) {
	const app = useContext(ApplicationContext) as Application;

	const { lMachineConfig } = useMachineConfig(app);

	const [lSlipSheetsPresent, lSetSlipSheetsPresent] = useState<boolean>(false);

	const [lCurrentStep, lSetCurrentStep] = useState<ConfigStep>(ConfigStep.Boxes); // do not directly set setup (instead use  links).

	let d = lSlipSheetsPresent;
	const [completionString, setCompletionString] = useState<string>('');
	const [lCompletionFraction, lSetCompletionFraction] = useState<number>(0);

	useEffect(() => {
		let lHas = false;
		lMachineConfig?.Bays.forEach(bay => {
			if (bay.Enabled) {
				bay.SlipSheetMags.forEach(m => {
					lHas = m || lHas;
				});
			}
		});
		lSetSlipSheetsPresent(lHas);
		const n = lCurrentStep + 1;
		const d = lHas ? AllSteps.length : AllSteps.length - 1;
		setCompletionString(`Step ${n}/${d}`);
		lSetCompletionFraction(d > 0 ? n / d : 0);
	}, [lMachineConfig]);

	const { path: lPath, url: lUrl } = useRouteMatch();
	const lLocation = useLocation();
	useEffect(
		function () {
			AllStepNames.forEach(function (pName, pIndex) {
				let lReg = new RegExp(pName, 'i');
				if (!lReg.test(lLocation.pathname)) return;
				lSetCurrentStep(pIndex);
			});
		},
		[lLocation]
	);

	const lBackLink = useRef(null),
		lNextLink = useRef(null);

	const stashEditedConfig = (pConfig: PalletConfiguration) => {
		const finalConfig = GenerateFinalConfig(pConfig);
		set_all_configs(finalConfig);
		let versionedConfig: any = finalConfig;
		versionedConfig['web_version'] = 1; // Decorate anything we put in localStorage with a recognizable version.
		localStorage.setItem(
			'vention_palletizer_configuration',
			JSON.stringify([versionedConfig])
		);
	};

	const lHandleSave = async function (pConfig: PalletConfiguration) {
		if (!app) return;
		if (app.api.isWebDeployment()) {
			stashEditedConfig(pConfig);
			return;
		}
		const finalConfig = GenerateFinalConfig(pConfig);
		try {
			await app.api.Pallet.update(finalConfig);
		} catch (pErr) {
			console.log(pErr);
		}

		back();
	};
	const lLocalReducer = useCallback(function (
		pConfig: PalletConfiguration,
		pAction: ConfigAction
	) {
		// Different form of auto-save, depending on the type of deployment.
		// In the web-only case, we don't want to save to file, we prefer to save to localStorage
		// (client-side - as a simple means of supporting multiple isolated users).
		const lNewState = configurationReducer(pConfig, pAction, app.api);
		lHandleSave(lNewState);
		return lNewState;
	},
	[]);

	const [configuration, dispatchConfiguration_a] = useReducer(
		lLocalReducer,
		id >= 0
			? all_configs.config
			: newPalletConfiguration('Configuration ' + String(1))
	);

	useEffect(() => {
		if (configuration.pallets.length === 0) {
			dispatchConfiguration_a({
				type: EnumConfAction.SetPallets,
				payload: getDefaultPallets() as any,
			});
		}
	}, [configuration]);

	const dispatchConfiguration = async (act: ConfigAction) => {
		/* console.log(`dispatch config`); */
		/* await app.api.Queue.delete(); */
		dispatchConfiguration_a(act);
	};

	const setBoxes = (boxes: Box_t[]) =>
		dispatchConfiguration({
			type: EnumConfAction.SetBoxes,
			payload: boxes as any,
		});

	const setPallets = function (pallets: Pallet_t[]) {
		dispatchConfiguration({
			type: EnumConfAction.SetPallets,
			payload: pallets as any,
		});

		app.api.Queue.delete().catch(err => {
			console.error('Error deleting queue after updating pallets.', err);
		});
	};
	const setSlipSheets = function (slipsheets: SlipSheet_t[]) {
		dispatchConfiguration({
			type: EnumConfAction.SetSlipSheets,
			payload: slipsheets as any,
		});
	};

	const lAllowedStep = getAllowedStep(configuration);

	function clearSlipsheetMapping() {
		// Clear ids from calibration
		app.api.Generic.get('CALIBRATION').then(function (data) {
			const c = to_calibration(data) as Calibration_t;
			c['Slip Sheet Pick Left 1'].ss_ids = [];
			c['Slip Sheet Pick Left 2'].ss_ids = [];
			c['Slip Sheet Pick Right 1'].ss_ids = [];
			c['Slip Sheet Pick Right 2'].ss_ids = [];

			app.api.Generic.set(['CALIBRATION'], c).catch(console.error);
		});
	}

	const handle_clear_all = async () => {
		if (
			window.confirm(
				'Your entire configuration will be reset.\n\nCancel and download your current configuration first if you are unsure.\n\nDo you wish to continue?'
			)
		) {
			await app.api.Queue.delete(); // clear queue.
			setBoxes([]);
			setSlipSheets([]);
			setPallets([]);
			clearSlipsheetMapping();
		}
	};

	const uploadFile = createRef<HTMLInputElement>();
	const handle_upload_init = () => {
		if (uploadFile.current) {
			uploadFile.current.value = '';
			uploadFile.current.click();
		}
	};

	const collectExistingIdsAndNames = (configuration: {
		boxes: Box_t[];
		slipsheets: SlipSheet_t[];
		pallets: Pallet_t[];
	}) => {
		const existingIdsSet = new Set<string>(); // Track all IDs in existing configuration
		const existingNames: {
			boxes: Set<string>;
			slipsheets: Set<string>;
			pallets: Set<string>;
			layouts: Set<string>;
			stacks: Set<string>;
		} = {
			boxes: new Set<string>(),
			slipsheets: new Set<string>(),
			pallets: new Set<string>(),
			layouts: new Set<string>(),
			stacks: new Set<string>(),
		};


		configuration.boxes.forEach(box => {
			existingIdsSet.add(box.id);
			existingNames.boxes.add(box.name || 'Unnamed Box');
		});
		configuration.slipsheets.forEach(sheet => {
			existingIdsSet.add(sheet.id);
			existingNames.slipsheets.add(sheet.name || 'Unnamed SlipSheet');
		});


		configuration.pallets.forEach(pallet => {
			existingIdsSet.add(pallet.id);


			pallet.Layouts.forEach(layout => {
				existingIdsSet.add(layout.id);
				existingNames.layouts.add(layout.name || 'Unnamed Layout');
				if (layout.box_id) existingIdsSet.add(layout.box_id);
			});


			pallet.Stack.forEach(stack => {
				existingIdsSet.add(stack.id);
				existingNames.stacks.add(stack.name || 'Unnamed Stack');
				stack.layers.forEach(layer => {
					if (layer.layout_id) existingIdsSet.add(layer.layout_id);
				});
			});
		});


		return { existingIdsSet, existingNames };
	};

	const handle_upload_term = () => {
		console.log('UPLOAD TERM');
		if (
			!uploadFile.current ||
			!uploadFile.current.files ||
			uploadFile.current.files.length === 0
		) {
			console.error('No file selected');
			return;
		}


		const file = uploadFile.current.files[0];
		const reader = new FileReader();

		let existingIdsSet = new Set<string>(); // Track all IDs in existing configuration
		let existingNames: {
			boxes: Set<string>;
			slipsheets: Set<string>;
			pallets: Set<string>;
			layouts: Set<string>;
			stacks: Set<string>;
		} = {
			boxes: new Set<string>(),
			slipsheets: new Set<string>(),
			pallets: new Set<string>(),
			layouts: new Set<string>(),
			stacks: new Set<string>(),
		};


		const replacementMap = new Map<string, string>(); // Map to track new IDs for duplicates


		// Collect all IDs and names from the existing configuration
		const { existingIdsSet: newExistingIdsSet, existingNames: newExistingNames } =
			collectExistingIdsAndNames(configuration);
		existingIdsSet = newExistingIdsSet;
		existingNames = newExistingNames;


		// Generate a new unique name if a duplicate is found
		const generateUniqueName = (
			name: string,
			type: keyof typeof existingNames
		): string => {
			let uniqueName = name;
			let suffix = 1;
			while (existingNames[type].has(uniqueName)) {
				const baseName = name.replace(/\(\d+\)$/, '');
				uniqueName = `${baseName} (${suffix++})`;
			}
			existingNames[type].add(uniqueName);
			return uniqueName;
		};

		// Generate a new ID for each duplicate found and update the name if needed
		const generateNewIdForDuplicate = (
			id: string,
			item: { name?: string },
			type: keyof typeof existingNames
		): string => {
			if (!replacementMap.has(id)) {
				const newId = uuidv4();
				replacementMap.set(id, newId);
				if (item.name && existingNames[type].has(item.name)) {
					item.name = generateUniqueName(item.name, type);
				} else if (item.name) {
					existingNames[type].add(item.name);
				}
				return newId;
			}
			return replacementMap.get(id)!;
		};

		// Replace duplicates in uploaded items, updating names and references as needed
		const checkAndReplaceDuplicates = <
			T extends { id: string; name?: string; box_id?: string; layout_id?: string }
		>(
			items: T[],
			type: keyof typeof existingNames
		): T[] => {
			return items.map(item => {
				const updatedItem = { ...item };
				if (existingIdsSet.has(updatedItem.id)) {
					updatedItem.id = generateNewIdForDuplicate(
						updatedItem.id,
						updatedItem,
						type
					);
				}
				if (updatedItem.box_id && existingIdsSet.has(updatedItem.box_id)) {
					updatedItem.box_id = generateNewIdForDuplicate(
						updatedItem.box_id,
						{},
						'boxes'
					);
				}
				if (updatedItem.layout_id && existingIdsSet.has(updatedItem.layout_id)) {
					updatedItem.layout_id = generateNewIdForDuplicate(
						updatedItem.layout_id,
						{},
						'layouts'
					);
				}
				return updatedItem;
			});
		};

		// Process pallets and update IDs for layouts and stacks, applying unique naming
		const processPallets = (newPallets: Pallet_t[]): Pallet_t[] => {
			return newPallets.map(pallet => {
				const updatedPallet = { ...pallet };
				if (existingIdsSet.has(updatedPallet.id)) {
					updatedPallet.id = generateNewIdForDuplicate(
						updatedPallet.id,
						updatedPallet,
						'pallets'
					);
				}
				updatedPallet.Layouts = checkAndReplaceDuplicates(
					updatedPallet.Layouts,
					'layouts'
				);
				updatedPallet.Stack = updatedPallet.Stack.map(stack => {
					const updatedStack = { ...stack };
					if (existingIdsSet.has(updatedStack.id)) {
						updatedStack.id = generateNewIdForDuplicate(
							updatedStack.id,
							updatedStack,
							'stacks'
						);
					}
					updatedStack.layers = updatedStack.layers.map(layer => {
						const updatedLayer = { ...layer };
						if (replacementMap.has(updatedLayer.layout_id)) {
							updatedLayer.layout_id = replacementMap.get(updatedLayer.layout_id)!;
						}
						return updatedLayer;
					});
					return updatedStack;
				});
				return updatedPallet;
			});
		};

		// Merge new pallets into existing pallets based on name and dimensions
		const mergeNewPallets = (
			existingPallets: Pallet_t[],
			newPallets: Pallet_t[]
		): Pallet_t[] => {
			const mergedPallets = [...existingPallets];
			newPallets.forEach(newPallet => {
				const match = mergedPallets.find(
					existingPallet =>
						existingPallet.name === newPallet.name &&
						JSON.stringify(existingPallet.Dimensions) ===
							JSON.stringify(newPallet.Dimensions)
				);
				if (match) {
					const existingLayoutIds = new Set(match.Layouts.map(layout => layout.id));
					const existingStackIds = new Set(match.Stack.map(stack => stack.id));
					const uniqueLayouts = newPallet.Layouts.filter(
						layout => !existingLayoutIds.has(layout.id)
					);
					const uniqueStacks = newPallet.Stack.filter(
						stack => !existingStackIds.has(stack.id)
					);
					match.Layouts = [...match.Layouts, ...uniqueLayouts];
					match.Stack = [...match.Stack, ...uniqueStacks];
				} else {
					mergedPallets.push(newPallet);
				}
			});
			return mergedPallets;
		};
		reader.onload = async (event: ProgressEvent<FileReader>) => {
			console.log('LOADED');
			try {
				const uploaded_config = JSON.parse(event.target!.result as string);
				if (
					!uploaded_config.config ||
					!uploaded_config.config.boxes ||
					!uploaded_config.config.slipsheets ||
					!uploaded_config.config.pallets
				) {
					throw new Error('Missing config attributes');
				}
				const boxes: Box_t[] = uploaded_config.config.boxes || [];
				const slipsheets: SlipSheet_t[] = uploaded_config.config.slipsheets || [];
				const pallets: Pallet_t[] = uploaded_config.config.pallets || [];

				collectExistingIdsAndNames(configuration);

				const uniqueBoxes = checkAndReplaceDuplicates(boxes, 'boxes');
				const uniqueSlipSheets = checkAndReplaceDuplicates(
					slipsheets,
					'slipsheets'
				);
				const uniquePallets = processPallets(pallets);

				const combinedBoxes = [...configuration.boxes, ...uniqueBoxes];
				const combinedSlipSheets = [
					...configuration.slipsheets,
					...uniqueSlipSheets,
				];
				const mergedPallets = mergeNewPallets(configuration.pallets, uniquePallets);

				// Update index after combining arrays
				const boxIndexMap = new Map(
					combinedBoxes.map((box, index) => [box.id, index])
				);
				const layoutIndexMap = new Map(
					mergedPallets.flatMap(pallet =>
						pallet.Layouts.map((layout, index) => [layout.id, index])
					)
				);

				// Update box positions and layer indices based on new box and layout indices
				const updatedPallets = mergedPallets.map(pallet => {
					const updatedLayouts = pallet.Layouts.map(layout => {
						if (layout.box_id && boxIndexMap.has(layout.box_id)) {
							const updatedPositions = layout.boxPositions.map(position => ({
								...position,
								index: boxIndexMap.get(layout.box_id)!,
							}));
							return { ...layout, boxPositions: updatedPositions };
						}
						return layout;
					});


					const updatedStacks = pallet.Stack.map(stack => {
						const updatedLayers = stack.layers.map(layer => {
							if (layer.layout_id && layoutIndexMap.has(layer.layout_id)) {
								return { ...layer, index: layoutIndexMap.get(layer.layout_id)! };
							}
							return layer;
						});
						return { ...stack, layers: updatedLayers };
					});


					return { ...pallet, Layouts: updatedLayouts, Stack: updatedStacks };
				});

				setBoxes(combinedBoxes);
				setPallets(updatedPallets);
				setSlipSheets(combinedSlipSheets);
				clearSlipsheetMapping();
			} catch (e: any) {
				alert(`Unrecognized file format: ${e.message}`);
			}
		};
		reader.readAsText(file);
	};

	const handle_download = () => {
		const finalConfig = GenerateFinalConfig(configuration);

		// For the web experience, download the json.
		var downloadLink = document.createElement('a');
		downloadLink.href = window.URL.createObjectURL(
			new Blob([JSON.stringify(finalConfig)], {
				type: 'application/json',
			})
		);
		downloadLink.download = 'pallet.json';
		downloadLink.click();
	};

	const controlProps: ControlProps = {
		handleBack: function () {
			if (lBackLink.current !== null) (lBackLink as any).current.click();
		},
		handleNext: function () {
			if (lNextLink.current !== null) (lNextLink as any).current.click();
		},
		instructionNumber: lCurrentStep + 1,
	};

	const lBoxesPath = lPath + getPathForStep(ConfigStep.Boxes);
	const { preferences } = useContext(PreferencesContext) as any;
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);
	const lSelectTrigger: SelectTrigger = useContext(SelectContext);
	const lItemProps: ConfigurationProps = {
		allBoxes: configuration.boxes,
		allPallets: configuration.pallets,
		allSlipSheets: configuration.slipsheets,
		setBoxes: setBoxes,
		setPallets: setPallets,
		setSlipSheets: setSlipSheets,
		triggerKeyboard: lKeyboardTrigger,
		triggerSelect: lSelectTrigger,
		unitsSystem: preferences.units_system,
		...controlProps,
	};

	const lBoxSizeProps: BoxConfigurationProps = {
		...lItemProps,
		disableNext: false,
		requireWeight: true,
		showLegend: true,
	};

	const lDisplayNone = { display: 'none' };

	return (
		<div className="full-width">
			{/* Hidden links for back + next. */}
			<Link
				style={lDisplayNone}
				to={lUrl + getPathForStep(Math.max(lCurrentStep - 1, 0))}
				innerRef={lBackLink}
			>
				Hidden Back Step
			</Link>
			<Link
				style={lDisplayNone}
				innerRef={lNextLink}
				to={
					lUrl +
					getPathForNextStep(
						Math.min(ConfigStep.Summary, lCurrentStep + 1),
						lSlipSheetsPresent
					)
				}
			>
				Hidden Next Step
			</Link>
			<main className="full-size flex-col">
				<Switch>
					<div className="full-size grid-col-18 vh-91">
						<div className="full-size flex-col align-center padding-top">
							<TableOfContents
								pAllowedStep={lAllowedStep}
								pCurrentStep={lCurrentStep}
								completionString={completionString}
								completionFraction={lCompletionFraction}
								handle_clear_all={handle_clear_all}
								handle_download={handle_download}
								handle_upload_init={handle_upload_init}
								handle_upload_term={handle_upload_term}
								uploadFile={uploadFile}
								pDisabledSlipSheets={!lSlipSheetsPresent}
							/>
						</div>
						<div className="full-size flex-center align-center">
							<div className="full-size">
								<Route exact path={lPath}>
									<Redirect to={lBoxesPath} />
								</Route>
								<RedirectGuard
									pBasePath={lPath}
									pAllowed={lAllowedStep}
									pTarget={ConfigStep.Boxes}
								>
									<BoxSize {...lBoxSizeProps} />
								</RedirectGuard>
								<RedirectGuard
									pBasePath={lPath}
									pAllowed={lAllowedStep}
									pTarget={ConfigStep.SlipSheets}
								>
									<SlipSheetSize {...lItemProps} />
								</RedirectGuard>
								<RedirectGuard
									pBasePath={lPath}
									pAllowed={lAllowedStep}
									pTarget={ConfigStep.Pallets}
								>
									<Pallets {...lItemProps} />
								</RedirectGuard>
								<RedirectGuard
									pBasePath={lPath}
									pAllowed={lAllowedStep}
									pTarget={ConfigStep.Layouts}
								>
									<Layout {...lItemProps} />
								</RedirectGuard>
								<RedirectGuard
									pBasePath={lPath}
									pAllowed={lAllowedStep}
									pTarget={ConfigStep.Stacks}
								>
									<Stack {...lItemProps} />
								</RedirectGuard>
								<RedirectGuard
									pBasePath={lPath}
									pAllowed={lAllowedStep}
									pTarget={ConfigStep.Summary}
								>
									<ConfigurationSummary {...lItemProps} />
								</RedirectGuard>
							</div>
						</div>
					</div>
					{/* <ContentSplit
						pContent={ChildElement}
						hideAside={!lShowSideBar}
						className={lShowSideBar ? 'grid-col-16' : 'full-size'}
					>
						<div className="Sidebar">
							<TableOfContents
								pAllowedStep={lAllowedStep}
								pCurrentStep={lCurrentStep}
							/>
							<div></div>

							
						</div>
					</ContentSplit> */}
				</Switch>
			</main>
		</div>
	);
}

interface RedirectGuardProps {
	pAllowed: ConfigStep;
	pTarget: ConfigStep;
	pBasePath: string;
	children: ReactElement;
}

export function RedirectGuard({
	pBasePath,
	pTarget,
	pAllowed,
	children,
}: RedirectGuardProps) {
	const lPath = pBasePath + getPathForStep(pTarget),
		lRedirect = pBasePath + getPathForStep(ConfigStep.Boxes),
		lRestrict = pTarget > pAllowed;

	return (
		<Route path={lPath} key={pTarget}>
			{lRestrict ? <Redirect to={lRedirect} /> : children}
		</Route>
	);
}

interface TableOfContentsProps {
	pCurrentStep: ConfigStep;
	pAllowedStep: ConfigStep;
	completionString: string;
	completionFraction: number;
	handle_clear_all: any;
	handle_download: any;
	handle_upload_init: any;
	handle_upload_term: any;
	uploadFile: any;
	pDisabledSlipSheets: boolean;
}

function TableOfContents({
	pCurrentStep,
	pAllowedStep,
	completionString,
	completionFraction,
	handle_clear_all,
	handle_download,
	handle_upload_init,
	handle_upload_term,
	uploadFile,
	pDisabledSlipSheets,
}: TableOfContentsProps) {
	const { url: lUrl } = useRouteMatch();

	return (
		<nav className="full-width-minus-pad full-height-minus-pad border rounded flex-col align-center flex-between">
			<div className="full-width-minus-pad flex-col align-center margin-top">
				{AllSteps.map((lStep: ConfigStep, lStepIndex: number) => {
					if (pDisabledSlipSheets && lStep === ConfigStep.SlipSheets) {
						return void 0;
					}
					const lRestrict = lStep > pAllowedStep,
						lName = AllStepNames[lStep],
						lOnClick = function (pEvent: React.MouseEvent) {
							if (lRestrict) pEvent.preventDefault();
						};
					let lClassName =
						'full-width background-silver rounded vh-8 flex-center color-white font-5 margin-bottom ' +
						(lStep === pCurrentStep
							? ' background-medium-blue color-blue bold-font blue-inner-border'
							: ' color-blue');
					if (lRestrict) lClassName += ' is-disabled';
					return (
						<Link
							className={lClassName}
							key={lStepIndex}
							to={lUrl + getPathForStep(lStep)}
							onClick={lOnClick}
						>
							{lName}
						</Link>
					);
				})}
				<footer className="full-width flex-center flex-col double-margin-top">
					<span className="bold-font">{completionString}</span>
					<progress
						className="margin-top full-width-minus-pad"
						max="1"
						value={completionFraction}
					></progress>
				</footer>
			</div>

			<div className="flex-col full-width-minus-pad flex-between align-center padding-bottom padding-top ">
				<div
					className="flex-col align-center full-width  background-medium-blue rounded half-margin-bottom"
					onClick={handle_download}
				>
					<span className="color-white padding">Download</span>
				</div>

				<div
					className="flex-col align-center full-width  background-medium-blue rounded half-margin-bottom"
					onClick={handle_upload_init}
				>
					<span className="color-white padding">Upload</span>
					<input
						type="file"
						id="file"
						ref={uploadFile}
						style={{ display: 'none' }}
						accept="*"
						onChange={handle_upload_term}
					/>
				</div>
				{/* {isWebDeployment() && (
					<div
						className="flex-col align-center full-width background-medium-blue rounded "
						onClick={handle_clear_all}
					>
						<span className="color-white padding">Clear</span>
					</div>
				)} */}
			</div>
		</nav>
	);
}
